import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import StarField from "../components/StarField";
import Footer from "../components/Footer";
import { BANNERS, NUMBER_COLORS } from "../data/banners";
import { api, setSeo, readCache, writeCache } from "../lib/api";

function viewOf(data, final = true) {
  const n = data?.number;
  if (n === null || n === undefined) return { display: "-", color: "#ffffff", shadow: "none" };
  const fc = NUMBER_COLORS[n % 3];
  return { display: String(n), color: fc, shadow: final ? `0 0 15px ${fc}80` : "none" };
}

export default function Home() {
  const [slide, setSlide] = useState(0);
  const [today, setToday] = useState(() => readCache("sle_today"));
  const [shown, setShown] = useState(null);
  const navigate = useNavigate();
  const target = today?.number;
  const { display, color, shadow } =
    target === null || target === undefined || shown === null ? viewOf(today) : viewOf({ number: shown }, shown === target);
  const liveTime = today?.time_slot || "--:-- --";

  // Count up 0 → final number whenever the final number changes
  useEffect(() => {
    if (target === null || target === undefined) return;
    let current = 0;
    setShown(0);
    const t = setInterval(() => {
      if (current < target) {
        current++;
        setShown(current);
      } else {
        clearInterval(t);
      }
    }, 400);
    return () => clearInterval(t);
  }, [target]);

  useEffect(() => {
    setSeo(
      "Shillong Lucky Ending Result Today | Live Lucky Lottery Number",
      "Shillong Lucky Ending live result today. Get fast, 100% verified Lucky Ending lucky lottery numbers, old results chart and pay gold win updates direct from the official admin panel."
    );
  }, []);

  // Banner auto-slide every 30s
  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % BANNERS.length), 30000);
    return () => clearInterval(t);
  }, []);

  // Fetch today's result; prefetch history so Old Results opens instantly
  useEffect(() => {
    api
      .get("/results/today")
      .then(({ data }) => {
        setToday(data);
        writeCache("sle_today", data);
      })
      .catch(() => {});
    api
      .get("/results/history")
      .then(({ data }) => writeCache("sle_history", data))
      .catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <StarField />
      <Header />

      {/* Banner Slider */}
      <div className="banner-wrapper" data-testid="banner-slider">
        {BANNERS.map((b, i) => (
          <div key={i} className={`slide ${i === slide ? "active" : ""}`}>
            <img
              className="banner-bg-image"
              src={b.img}
              alt={`Banner ${i + 1}`}
              loading={i === 0 ? "eager" : "lazy"}
              decoding="async"
              fetchPriority={i === 0 ? "high" : "low"}
            />
            <div className="banner-text-middle">{b.text}</div>
          </div>
        ))}
      </div>

      {/* Result */}
      <div className="result-section">
        <div className="animated-ticket-border">
          <div className="ticket-box-inner">
            <div className="result-title">Shillong Lucky Ending</div>
            <div className="live-button-container">
              <div className="live-btn-pill">
                <span className="live-dot"></span>
                <span className="live-text-italic">LIVE</span>
              </div>
              <span className="time-text" data-testid="live-time">{liveTime}</span>
            </div>
            <div className="number-wrapper">
              <div
                className="result-number"
                id="final-result-box"
                data-testid="result-number"
                style={{ color, textShadow: shadow }}
              >
                {display}
              </div>
            </div>
          </div>
        </div>

        <div className="buttons-group">
          <button className="btn-old-result" onClick={() => navigate("/all-result-list")} data-testid="old-result-btn">
            All Result 📜
          </button>
        </div>
      </div>

      {/* SEO Content */}
      <div className="content-area">
        <div className="content-inner" data-testid="seo-content">
          <h1>Shillong Lucky Ending Result Today</h1>
          <p>
            Welcome to the most trusted and officially updated platform for the{" "}
            <strong>Shillong Lucky Ending</strong> results. Our portal is dedicated to providing you with{" "}
            <strong>real-time, fast, and 100% accurate</strong> information directly synchronized with our admin
            panel. The moment the final fifteen minutes of the game approach, excitement peaks across every corner
            of Meghalaya, and we guarantee that our website will always be among the first to display the updated{" "}
            <strong>Lucky Ending</strong> digit for the day. Whether you follow the game casually or track every
            single slot religiously, this page is designed to give you the cleanest, quickest and most reliable
            experience possible for the famous <strong>lucky lottery</strong> of Shillong. New here? Read{" "}
            <Link className="inlink" to="/about">about the game, rules and prize structure</Link> first.
          </p>

          <p>
            The{" "}
            <Link className="inlink" to="/all-result-list">
              Shillong Teer game
            </Link>{" "}
            is deeply rooted in the traditional archery heritage of Meghalaya, India. In this game, authentic archery
            skill meets strategic numerical calculation. While the standard{" "}
            <strong>First Round (FR)</strong> and <strong>Second Round (SR)</strong> results are widely anticipated,
            the Lucky Ending holds a very special place for our community. It acts as the ultimate decider for many
            enthusiasts who believe the concluding digit carries the real magic. On this page, we focus purely on
            delivering that concluding excitement, with a bold, premium single-digit display that leaves no room for
            confusion.
          </p>

          <h2>How the Shillong Lucky Ending System Works</h2>
          <p>
            The Lucky Ending number is a <strong>single-digit outcome</strong> ranging from 0 to 9. This number is
            derived mathematically from the total arrows hitting the target during the live archery session. Our{" "}
            <strong>automated digital system</strong> captures this exact value at the moment of declaration and
            pushes it instantly to your screen. The ticket box above features an animated glowing border, and the
            final digit is displayed the moment the page loads, so you always see the{" "}
            <strong>declared result</strong> without any delay.
          </p>
          <p>
            For complete transparency, our live update timer indicates exactly which slot the number belongs to. We
            believe in fair play and accurate reporting at all times. There are no deliberate delays, no manual
            errors, and absolutely no manipulated numbers. Whatever the official archery association declares is
            exactly what you get on your screen within milliseconds. This commitment to honesty is why thousands of
            <strong> Lucky Ending</strong> followers return to our platform every single day.
          </p>

          <h2>Manual and Automatic Result Updates</h2>
          <p>
            One of the biggest advantages of our portal is flexibility. Our secure admin panel allows the operator to
            either enter each result manually for complete control, or switch on an automatic mode that updates the
            elapsed time slots instantly without any human intervention. This hybrid approach means that during busy
            days the system keeps running on its own, while on important sessions the admin can personally verify and
            key in the exact winning digit. This blend of manual precision and automation is designed to keep the{" "}
            <strong>lucky lottery</strong> experience smooth, accurate and uninterrupted from 09:00 AM right through
            to 08:00 PM.
          </p>

          <h2>Game Rules and Safe Play Guidelines</h2>
          <p>
            Before participating or tracking the numbers, it is essential to understand the rules of the game.
            Shillong Teer and its Lucky Ending digits are shared strictly for informational and entertainment
            purposes on this site. Here are some basic rules and guidelines every visitor should keep in mind so
            that the experience stays positive, responsible and respectful of local tradition.
          </p>
          <p>
            <strong>Official Timings:</strong> The game operates on a fixed daily schedule. The first round is usually
            declared around 10:30 AM and the second round around 11:30 AM. However, the Lucky Ending digit focuses on
            the final calculation window, which is why our live board tracks every fifteen-minute slot of the day.
          </p>
          <p>
            <strong>Age Restriction:</strong> You must be <strong>18 years or older</strong> to participate in any
            real-world activities related to this archery sport. We strongly discourage minors from engaging with any
            form of the game.
          </p>
          <p>
            <strong>Responsible Play:</strong> We highly encourage responsible behaviour at all times. Never treat
            common numbers or past patterns as guaranteed outcomes, because the game is completely based on the
            physical result of a live archery session. Play for the thrill and the culture, never beyond your means,
            and always remember that there is no guaranteed way to <strong>pay gold win</strong> in any lottery. For
            the complete rules please read our <Link className="inlink" to="/terms">Terms &amp; Conditions</Link>.
          </p>

          <h2>Understanding the Significance of the Ending Number</h2>
          <p>
            Many loyal followers of the game build their strategy using calculations based on previous results,
            common numbers and the famous "Dream Numbers" of Khasi tradition. The Ending digit is simply the last
            number of the two-digit result. For example, if the round result is 45, the ending digit is 5. Predicting
            or guessing that ending digit is a hugely popular pastime among the locals, and entire communities
            discuss their reasoning before every session. On this specific page, we highlight that singular, final
            ending digit to save your valuable time. You do not need to scroll through massive lists or confusing
            tables here; a clean, premium and bold single-digit display is always our top priority.
          </p>

          <h2>Why Analysing Previous Results Matters</h2>
          <p>
            Seasoned players spend a lot of time looking at a{" "}
            <Link className="inlink" to="/all-result-list">chart of old numbers</Link>, and there is real logic behind
            it. The answer lies in the concept of <strong>frequency and historical patterns</strong>. A quick glance at
            recent days helps you identify which digits are hitting frequently, known as <strong>hot numbers</strong>,
            and which ones have been absent for a while, known as <strong>cold numbers</strong>. Maintaining an
            archived record also ensures maximum transparency, so
            you can cross-check our past records against official announcements and verify the accuracy of our updates
            for yourself. Instead of relying purely on luck, accessing continuous historical data allows true
            enthusiasts to apply their own mathematical formulas and personal calculations with confidence.
          </p>

          <h2>Why Choose Our Shillong Lucky Ending Portal</h2>
          <p>
            The internet is flooded with websites claiming to provide Teer and <strong>Lucky Ending</strong> results,
            but very few offer a seamless, user-friendly and secure environment. We have carefully designed this
            portal with a dark premium theme, dynamic multicolour star animations and colour-coded numbers to make
            every visit feel special. Our servers are <strong>optimised for high traffic</strong>, meaning that even
            if thousands of users check the result at the exact same second, our website will remain stable and fast.
            Mobile users enjoy a perfectly responsive layout, and the horizontally scrollable results chart makes it
            effortless to study every slot on a small screen.
          </p>

          <h2>Frequently Asked Questions (FAQs)</h2>
          <p>
            <strong>Q1: How fast is the Shillong Lucky Ending result updated here?</strong>
            <br />
            A: The result is updated <strong>instantly</strong> through our admin panel. As soon as the archery
            counting is finalised, our server pushes the fresh data straight to this page with no waiting.
          </p>
          <p>
            <strong>Q2: Why does the number appear instantly?</strong>
            <br />
            A: The final digit is fetched directly from our server and shown immediately on page load — no fake
            loading or counting animation, just the <strong>declared result</strong> for the current slot.
          </p>
          <p>
            <strong>Q3: Are common numbers 100% accurate?</strong>
            <br />
            A: No. Common numbers are simply <strong>mathematical predictions</strong> based on past trends and
            history. They are never guaranteed winning numbers, so always play responsibly.
          </p>

          <p>
            Keep visiting our page daily, bookmark it in your browser, and share it with your friends for the quickest
            updates. Questions or feedback? Visit our <Link className="inlink" to="/contact">Contact Us</Link> page,
            and see how we protect your data in our <Link className="inlink" to="/privacy">Privacy Policy</Link>. May
            the odds be in your favour, and welcome once again to the ultimate destination for the{" "}
            <strong>Shillong Lucky Ending</strong> — the premium home of the lucky lottery and the fastest Lucky
            Ending results online!
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
}
